const STEP_KEYS = { ArrowDown: 1, ArrowUp: -1 };

// Only the menu's own items — a submenu is its own `role="menu"`, so its items stay out.
export const getMenuItems = (menu) =>
  Array.from(menu?.querySelectorAll('[role="menuitem"]') ?? []).filter(
    (item) => item.closest('[role="menu"]') === menu
  );

export const focusFirstMenuItem = (menu, preferredItem) => {
  const item =
    preferredItem && menu?.contains(preferredItem) ? preferredItem : getMenuItems(menu)[0];
  item?.focus({ preventScroll: true });
};

const getNextIndex = (key, index, count) => {
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (index === -1) return STEP_KEYS[key] > 0 ? 0 : count - 1;
  return (index + STEP_KEYS[key] + count) % count;
};

/**
 * Keyboard support for a `role="menu"` container: arrows/Home/End move focus between items,
 * Enter/Space activate the focused item. Handled keys stop propagating — submenus are portaled
 * but still bubble through the React tree into their parent menu.
 */
export const handleMenuKeyDown = (event) => {
  const { key, target, currentTarget: menu } = event;
  // Options can embed their own fields (`option.component`); leave their keys alone.
  if (target.matches?.('input, textarea, select, [contenteditable]')) return;
  const items = getMenuItems(menu);
  if (!items.length) return;

  if (key === 'Enter' || key === ' ') {
    if (!items.includes(target)) return;
    event.preventDefault();
    event.stopPropagation();
    target.click();
    return;
  }

  if (!(key in STEP_KEYS) && key !== 'Home' && key !== 'End') return;

  event.preventDefault();
  event.stopPropagation();
  const index = items.findIndex((item) => item.contains(document.activeElement));
  items[getNextIndex(key, index, items.length)].focus();
};

// On an item with children: ArrowRight/Enter/Space open its submenu.
export const handleSubmenuTriggerKeyDown = (event, openSubmenu) => {
  if (event.target !== event.currentTarget) return;
  if (!['ArrowRight', 'Enter', ' '].includes(event.key)) return;
  event.preventDefault();
  event.stopPropagation();
  openSubmenu();
};

// Inside a submenu: ArrowLeft closes it and returns focus to the item that opened it.
export const handleSubmenuKeyDown = (event, parentItem, closeSubmenu) => {
  if (event.key !== 'ArrowLeft') return handleMenuKeyDown(event);
  event.preventDefault();
  event.stopPropagation();
  // Focus moves first — unmounting the submenu while it holds focus would drop focus to <body>.
  parentItem?.focus();
  closeSubmenu?.();
};
